import { Component, signal, HostListener, ChangeDetectionStrategy, isDevMode } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PartnerService } from '../../../core/services/partner.service';
import { PartnerApiService } from '../../../core/services/partner-api.service';
import { LogoComponent } from '../../../shared/components/logo/logo.component';
import { MixpanelService } from '../../../core/services/mixpanel.service';
import {
  PARTNER_VALIDATION as V,
  PARTNER_VALIDATION_MESSAGES as VM,
  PARTNER_COUNTRIES,
  isValidPartnerPassword,
} from '../../../shared/constants/partner-validation.constants';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule, FormsModule, LogoComponent],
  templateUrl: './landing.page.html',
  styleUrl: './landing.page.css',
  changeDetection: ChangeDetectionStrategy.Default
})
export class LandingPage {
  // Scrolled state for Header
  protected readonly isScrolled = signal(false);
  
  // Modals Open State
  protected readonly isContactOpen = signal(false);
  protected readonly isLoginOpen = signal(false);
  protected readonly isStandardLoginOpen = signal(false);
  protected readonly isGetStartedOpen = signal(false);
  
  // Mobile menu drawer
  protected readonly mobileMenuOpen = signal(false);

  // Countries list for selection (shared with backend validation)
  protected readonly countries = PARTNER_COUNTRIES;

  // Contact Form Model
  protected contactForm = {
    name: '', company_name: '', email: '', phone: '', address: '', country: '', company_identity_number: '', message: ''
  };
  protected readonly contactErrors = signal<Record<string, string>>({});
  protected readonly isSubmittingContact = signal(false);
  protected readonly contactSubmitted = signal(false);
  protected readonly referenceProtocolId = signal('');

  // Get Started Wizard Form
  protected startedForm = {
    fullName: '', businessEmail: '', password: '',
    partnerType: 'integration', platformName: '', apiUsage: 'read-write',
    agreeToTerms: false
  };
  protected readonly startedStep = signal(1);
  protected readonly startedErrors = signal<Record<string, string>>({});
  protected readonly isSubmittingStarted = signal(false);
  protected readonly startedSubmitted = signal(false);
  protected readonly startedSecretToken = signal('');

  // Login form state
  protected readonly authTab = signal<'signin' | 'signup'>('signin');
  protected loginEmail = '';
  protected loginPassword = '';
  protected standardLoginEmail = '';
  protected standardLoginPassword = '';
  protected readonly standardLoginErrors = signal<Record<string, string>>({});
  protected readonly isSubmittingStandardLogin = signal(false);
  protected readonly loginErrors = signal<Record<string, string>>({});
  protected readonly isSubmittingLogin = signal(false);
  protected readonly loginSuccess = signal(false);
  protected readonly loginNodeId = signal('');
  protected readonly showPassword = signal(false);
  protected readonly isStandardLoginOtp = signal(false);
  private tempToken = '';
  private tempNode = '';

  // Forgot password flow states
  protected readonly isForgotPassword = signal(false);
  protected readonly forgotPasswordStep = signal<1 | 2>(1);
  protected forgotPasswordEmail = '';
  protected readonly forgotPasswordOtpCode = signal<string[]>(['', '', '', '', '', '']);
  protected forgotPasswordNewPassword = '';

  protected forgotPasswordConfirmPassword = '';
  protected readonly isSubmittingForgotPassword = signal(false);
  protected readonly isResettingPassword = signal(false);
  protected readonly forgotPasswordErrors = signal<Record<string, string>>({});
  protected readonly showForgotPasswordNewPassword = signal(false);
  protected readonly showForgotPasswordConfirmPassword = signal(false);

  get isForgotPasswordNewPasswordValid(): boolean {
    return isValidPartnerPassword(this.forgotPasswordNewPassword);
  }

  get hasEightCharacters(): boolean {
    return (this.loginPassword || '').length >= 8;
  }

  get hasUppercase(): boolean {
    return /[A-Z]/.test(this.loginPassword || '');
  }

  get hasLowercase(): boolean {
    return /[a-z]/.test(this.loginPassword || '');
  }

  get hasSpecialChar(): boolean {
    return V.PASSWORD.SPECIAL_REGEX.test(this.loginPassword || '');
  }

  get hasEightForgotPassword(): boolean {
    return (this.forgotPasswordNewPassword || '').length >= 8;
  }

  get hasUppercaseForgotPassword(): boolean {
    return /[A-Z]/.test(this.forgotPasswordNewPassword || '');
  }

  get hasLowercaseForgotPassword(): boolean {
    return /[a-z]/.test(this.forgotPasswordNewPassword || '');
  }

  get hasSpecialForgotPassword(): boolean {
    return V.PASSWORD.SPECIAL_REGEX.test(this.forgotPasswordNewPassword || '');
  }


  // OTP Verification flow signals
  protected readonly loginStep = signal<'credentials' | 'otp'>('credentials');
  protected readonly otpCode = signal<string[]>(['', '', '', '', '', '']);
  protected readonly otpError = signal<string>('');
  protected readonly isSubmittingOtp = signal(false);
  protected readonly isResendingOtp = signal(false);

  protected togglePasswordVisibility() {
    this.showPassword.set(!this.showPassword());
  }

  protected doesUserExist(): boolean {
    if (!this.loginEmail) return false;
    return this.partnerService.users().some(u => u.email.toLowerCase() === this.loginEmail.toLowerCase());
  }

  protected onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // Only allow single digit numbers
    if (value && !/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    const currentOtp = [...this.otpCode()];
    currentOtp[index] = value;
    this.otpCode.set(currentOtp);
    this.otpError.set('');

    // Auto-focus next input
    if (value && index < 5) {
      const idPrefix = this.isStandardLoginOtp() ? 'standard-otp-' : 'otp-';
      const nextInput = document.getElementById(`${idPrefix}${index + 1}`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  protected onOtpKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpCode()[index] && index > 0) {
      const idPrefix = this.isStandardLoginOtp() ? 'standard-otp-' : 'otp-';
      const prevInput = document.getElementById(`${idPrefix}${index - 1}`) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
        const currentOtp = [...this.otpCode()];
        currentOtp[index - 1] = '';
        this.otpCode.set(currentOtp);
      }
    }
  }

  protected onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    
    const pastedText = clipboardData.getData('text').trim();
    if (!pastedText) return;
    
    // Extract only digits
    const digits = pastedText.replace(/\D/g, '').split('').slice(0, 6);
    if (digits.length === 0) return;
    
    const currentOtp = [...this.otpCode()];
    for (let i = 0; i < digits.length; i++) {
      currentOtp[i] = digits[i];
    }
    this.otpCode.set(currentOtp);
    this.otpError.set('');
    
    // Auto focus the next empty input or the last input
    const idPrefix = this.isStandardLoginOtp() ? 'standard-otp-' : 'otp-';
    const nextIndex = Math.min(digits.length, 5);
    const nextInput = document.getElementById(`${idPrefix}${nextIndex}`) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }
  }

  protected handleOtpSubmit() {
    const code = this.otpCode().join('');
    const password = this.loginPassword;
    const errors: Record<string, string> = {};

    if (!password) {
      errors['password'] = 'Password is required';
    } else if (!this.doesUserExist()) {
      if (!isValidPartnerPassword(password)) {
        errors['password'] = VM.PASSWORD;
      }
    }

    this.loginErrors.set(errors);

    if (code.length < 6) {
      this.otpError.set('Verification code is required');
      return;
    } else {
      this.otpError.set('');
    }

    if (Object.keys(errors).length > 0) {
      return;
    }

    this.isSubmittingOtp.set(true);
    const payload = {
      email: this.loginEmail,
      code: code,
      new_password: password
    };

    this.partnerApiService.activatePartner(payload).subscribe({
      next: (response) => {
        this.isSubmittingOtp.set(false);

        const email = this.loginEmail.toLowerCase();
        const userExists = this.partnerService.users().some(u => u.email.toLowerCase() === email);

        if (!userExists) {
          const emailPrefix = this.loginEmail.split('@')[0];
          const userName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
          const newUser = {
            id: 'usr_' + Math.random().toString(36).substring(2, 6),
            name: userName,
            email: this.loginEmail,
            joinedDate: new Date().toISOString().split('T')[0],
            status: 'Active',
            portfolio: '$10,000',
            txCount: 0
          };
          
          this.partnerService.users.update(list => [...list, newUser]);

          const currentCount = parseInt(this.partnerService.stats.totalUsers.replace(/,/g, '')) || 0;
          this.partnerService.stats.totalUsers = (currentCount + 1).toLocaleString();

          // this.partnerService.showToast(`Welcome! Account created successfully for ${this.loginEmail}.`);
        } else {
          this.partnerService.showToast(`Logged in successfully as ${this.loginEmail}.`);
        }

        const node = 'hs_partner_node_' + Math.random().toString(36).substring(2, 8);
        const token = response?.token || response?.data?.token || response?.data?.accessToken;
        const csrf = response?.data?.csrf_token;
        this.partnerService.handleEnterConsole(node, token, csrf);
        this.closeAllModals();
        this.router.navigate(['/app/overview']);
      },
      error: (err) => {
        if (isDevMode()) {
          console.error('Partner activation request failed:', err);
        }
        this.isSubmittingOtp.set(false);
        const errorMsg = err.error?.metadata?.message || err.error?.message || '';
        if (errorMsg.toLowerCase().includes('activated') || errorMsg.toLowerCase().includes('already') || err.status === 409) {
          this.partnerService.showToast('already activated this account go to login page', 'error');
        } else if (errorMsg) {
          this.partnerService.showToast(errorMsg, 'error');
        } else {
          this.partnerService.showToast('Invalid verification code or server error. Please try again.', 'error');
        }
      }
    });
  }

  protected resendLoginOtp() {
    if (!this.loginEmail) return;
    this.isResendingOtp.set(true);
    this.partnerApiService.requestOtp(this.loginEmail).subscribe({
      next: () => {
        this.isResendingOtp.set(false);
        this.partnerService.showToast('Verification code resent successfully.', 'success');
      },
      error: (err) => {
        this.isResendingOtp.set(false);
        const errorMsg = err.error?.metadata?.message || err.error?.message || 'Failed to resend verification code. Please try again.';
        this.partnerService.showToast(errorMsg, 'error');
      }
    });
  }

  constructor(
    protected readonly partnerService: PartnerService,
    private readonly partnerApiService: PartnerApiService,
    private readonly router: Router,
    private readonly mixpanelService: MixpanelService
  ) {
    // If already logged in, redirect to app panel
    if (this.partnerService.isLoggedIn()) {
      this.router.navigate(['/app/overview']);
    }
  }

  // Host Listener for scrolling effect on navbar
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (window.scrollY > 50) {
      this.isScrolled.set(true);
    } else {
      this.isScrolled.set(false);
    }
  }

  // Set modal scroll lock in html body
  private updateScrollLock(lock: boolean) {
    if (lock) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  // Open modals helper
  protected openContact() {
    this.isContactOpen.set(true);
    this.updateScrollLock(true);
  }

  protected openLogin(tab: 'signin' | 'signup' = 'signin') {
    this.authTab.set(tab);
    this.isLoginOpen.set(true);
    this.updateScrollLock(true);
  }

  protected openStandardLogin() {
    this.closeAllModals();
    this.isStandardLoginOpen.set(true);
    this.updateScrollLock(true);
  }

  protected handleStandardLoginSubmit() {
    const errors: Record<string, string> = {};
    if (!this.standardLoginEmail) {
      errors['email'] = 'Enter a valid email address';
    } else if (!this.validateEmail(this.standardLoginEmail)) {
      errors['email'] = 'Enter a valid email address';
    }
    
    if (!this.standardLoginPassword) {
      errors['password'] = 'Password is required.';
    }

    this.standardLoginErrors.set(errors);

    if (Object.keys(errors).length === 0) {
      this.isSubmittingStandardLogin.set(true);
      const payload = {
        email: this.standardLoginEmail,
        password: this.standardLoginPassword
      };

      this.partnerApiService.loginPartner(payload).subscribe({
        next: (response) => {
          this.isSubmittingStandardLogin.set(false);
          const requiresTwoFactor = response?.requiresTwoFactor || response?.data?.requiresTwoFactor;
          
          if (requiresTwoFactor) {
            this.tempToken = '';
            this.tempNode = '';
            // Switch to Security Verification view
            this.isStandardLoginOtp.set(true);
            this.otpCode.set(['', '', '', '', '', '']);
            this.otpError.set('');
          } else {
            const token = response?.token || response?.data?.token || response?.data?.accessToken;
            const csrf = response?.data?.csrf_token;
            this.tempToken = token || '';
            this.tempNode = token ? 'hs_partner_' + token.substring(0, 8) : 'hs_partner_node_' + Math.random().toString(36).substring(2, 8);

            // Bypass OTP and log in directly
            this.partnerService.showToast('Logged in successfully.');
            this.partnerService.handleEnterConsole(this.tempNode, this.tempToken, csrf);
            this.mixpanelService.identify(this.standardLoginEmail, {
              $email: this.standardLoginEmail,
              node_id: this.tempNode,
            });
            this.mixpanelService.track('partner_signed_in', { method: 'email' });
            this.closeAllModals();
            this.router.navigate(['/app/overview']);
          }

        },
        error: (err) => {
          if (isDevMode()) {
            console.error('Partner login failed:', err);
          }
          this.isSubmittingStandardLogin.set(false);
          const errMsg = err.error?.metadata?.message || err.error?.message || 'Invalid email or password. Please try again.';
          this.partnerService.showToast(errMsg, 'error');
        }
      });
    }
  }

  protected handleStandardOtpSubmit() {
    const code = this.otpCode().join('');
    if (code.length < 6) {
      this.otpError.set('Verification code is required');
      return;
    } else {
      this.otpError.set('');
    }

    this.isSubmittingOtp.set(true);

    const payload = {
      email: this.standardLoginEmail,
      password: this.standardLoginPassword,
      code: code
    };

    this.partnerApiService.loginPartnerWith2FA(payload).subscribe({
      next: (response) => {
        this.isSubmittingOtp.set(false);
        const token = response?.token || response?.data?.token || response?.data?.accessToken;
        const csrf = response?.data?.csrf_token;
        this.tempToken = token || '';
        this.tempNode = token ? 'hs_partner_' + token.substring(0, 8) : 'hs_partner_node_' + Math.random().toString(36).substring(2, 8);

        this.partnerService.showToast('Logged in successfully.');
        this.partnerService.handleEnterConsole(this.tempNode, this.tempToken, csrf);
        this.mixpanelService.identify(this.standardLoginEmail, {
          $email: this.standardLoginEmail,
          node_id: this.tempNode,
        });
        this.mixpanelService.track('partner_signed_in', { method: 'email_2fa' });
        this.closeAllModals();
        this.router.navigate(['/app/overview']);
      },
      error: (err) => {
        this.isSubmittingOtp.set(false);
        const errorMsg = err.error?.metadata?.message || err.error?.message || 'Invalid or expired verification code';
        this.otpError.set(errorMsg);
        this.partnerService.showToast(errorMsg, 'error');
      }
    });
  }

  protected openGetStarted() {
    this.isGetStartedOpen.set(true);
    this.updateScrollLock(true);
  }

  protected toggleMobileMenu() {
    this.mobileMenuOpen.set(!this.mobileMenuOpen());
  }

  // Tracks whether the contact form was submitted at least once. Inline errors
  // only appear after the first submit, then update live as each field is fixed.
  private contactSubmitAttempted = false;
  private readonly contactFieldKeys = [
    'name', 'company_name', 'email', 'phone',
    'address', 'country', 'company_identity_number', 'message',
  ];

  /** Returns the validation error for a single contact field, or '' if valid. */
  private computeContactFieldError(field: string): string {
    const form = this.contactForm;
    switch (field) {
      case 'name': {
        const name = form.name.trim();
        if (!name) return 'UBO/ Director Full Name is required';
        if (name.length < V.NAME.MIN || name.length > V.NAME.MAX || !V.NAME.REGEX.test(name)) return VM.NAME;
        return '';
      }
      case 'company_name': {
        const companyName = form.company_name.trim();
        if (!companyName) return 'Company Name is required';
        if (
          companyName.length < V.COMPANY_NAME.MIN ||
          companyName.length > V.COMPANY_NAME.MAX ||
          !V.COMPANY_NAME.REGEX.test(companyName)
        ) return VM.COMPANY_NAME;
        return '';
      }
      case 'email': {
        const email = form.email.trim();
        if (!email) return 'Email is required';
        if (!this.validateEmail(email) || email.length > V.EMAIL.MAX) return VM.EMAIL;
        return '';
      }
      case 'phone': {
        if (!form.phone.trim()) return 'Phone number is required';
        if (!this.validatePhone(form.phone)) return VM.PHONE;
        return '';
      }
      case 'address': {
        const address = form.address.trim();
        if (!address) return 'Address is required';
        if (address.length < V.ADDRESS.MIN || address.length > V.ADDRESS.MAX) return VM.ADDRESS;
        return '';
      }
      case 'country': {
        if (!form.country || !PARTNER_COUNTRIES.includes(form.country)) return VM.COUNTRY;
        return '';
      }
      case 'company_identity_number': {
        const companyId = form.company_identity_number.trim();
        if (!companyId) return 'Company Identity Number is required';
        if (
          companyId.length < V.COMPANY_IDENTITY_NUMBER.MIN ||
          companyId.length > V.COMPANY_IDENTITY_NUMBER.MAX ||
          !V.COMPANY_IDENTITY_NUMBER.REGEX.test(companyId)
        ) return VM.COMPANY_IDENTITY_NUMBER;
        return '';
      }
      case 'message': {
        if (!form.message.trim()) return 'Inquiry message is required';
        if (form.message.trim().length > V.MESSAGE.MAX) return VM.MESSAGE;
        return '';
      }
      default:
        return '';
    }
  }

  /** Re-validates one contact field as the user edits it (only after first submit). */
  protected revalidateContactField(field: string) {
    if (!this.contactSubmitAttempted) return;
    const message = this.computeContactFieldError(field);
    const errors = { ...this.contactErrors() };
    if (message) {
      errors[field] = message;
    } else {
      delete errors[field];
    }
    this.contactErrors.set(errors);
  }

  // Contact US Form Submission
  protected handleContactSubmit() {
    this.contactSubmitAttempted = true;
    const form = this.contactForm;
    const errors: Record<string, string> = {};
    for (const field of this.contactFieldKeys) {
      const message = this.computeContactFieldError(field);
      if (message) {
        errors[field] = message;
      }
    }

    this.contactErrors.set(errors);

    if (Object.keys(errors).length === 0) {
      this.isSubmittingContact.set(true);

      /**
       * Construct the standardized API payload.
       * This payload is designed to map perfectly to the required backend
       * schema for the WazirX partner integration requirements.
       */
      const payload = {
        name: form.name,
        company_name: form.company_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        country: form.country,
        company_identity_number: form.company_identity_number,
        message: form.message
      };

      // Initiate the POST request via our partner API service

      this.partnerApiService.submitPartnerRequest(payload).subscribe({
        next: (response) => {
          this.isSubmittingContact.set(false);
          this.referenceProtocolId.set('#HB-' + Math.floor(100000 + Math.random() * 900000));
          this.contactSubmitted.set(true);
          this.partnerService.showToast('Partner request submitted successfully!');
        },
        error: (err) => {
          if (isDevMode()) {
            console.error('Partner request submission failed:', err);
          }
          this.isSubmittingContact.set(false);
          const apiMessage =
            err?.error?.message ||
            err?.error?.metadata?.message ||
            err?.error?.error ||
            'Failed to submit request. Please try again later.';
          this.partnerService.showToast(apiMessage, 'error');
        }
      });
    }
  }



  // Get Started Steps Onboarding
  protected handleNextStep() {
    const errors: Record<string, string> = {};
    const step = this.startedStep();
    const form = this.startedForm;

    if (step === 1) {
      if (!form.fullName.trim()) errors['fullName'] = 'Full name is required';
      if (!form.businessEmail.trim()) {
        errors['businessEmail'] = 'Business email is required';
      } else if (!this.validateEmail(form.businessEmail)) {
        errors['businessEmail'] = 'Enter a valid business email';
      }
      if (!form.password) {
        errors['password'] = 'Password is required';
      } else if (!isValidPartnerPassword(form.password)) {
        errors['password'] = VM.PASSWORD;
      }
    }

    if (step === 3) {
      if (!form.platformName.trim()) errors['platformName'] = 'Platform name is required';
      if (!form.agreeToTerms) errors['agreeToTerms'] = 'You must agree to the Terms & Conditions';
    }

    this.startedErrors.set(errors);

    if (Object.keys(errors).length === 0) {
      if (step < 3) {
        this.startedStep.set(step + 1);
      } else {
        this.isSubmittingStarted.set(true);
        setTimeout(() => {
          this.isSubmittingStarted.set(false);
          this.startedSecretToken.set('hs_live_pk_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
          this.startedSubmitted.set(true);
        }, 1500);
      }
    }
  }

  protected handleBackStep() {
    const step = this.startedStep();
    if (step > 1) {
      this.startedStep.set(step - 1);
    } else {
      this.closeAllModals();
    }
  }

  protected selectPartnerType(type: string) {
    this.startedForm = {
      ...this.startedForm,
      partnerType: type
    };
  }

  // Enter Console Trigger
  protected handleEnterConsole() {
    const node = this.loginNodeId() || 'hs_partner_node_' + Math.random().toString(36).substring(2, 8);
    this.partnerService.handleEnterConsole(node);
    this.closeAllModals();
    this.router.navigate(['/app/overview']);
  }

  protected handleLoginSubmit() {
    const errors: Record<string, string> = {};
    const email = this.loginEmail;

    if (!email.trim()) {
      errors['email'] = 'Email is required';
    } else if (!this.validateEmail(email)) {
      errors['email'] = 'Enter a valid email';
    }

    this.loginErrors.set(errors);

    if (Object.keys(errors).length === 0) {
      this.isSubmittingLogin.set(true);
      
      this.partnerApiService.requestOtp(email).subscribe({
        next: (response) => {
          this.isSubmittingLogin.set(false);
          this.loginStep.set('otp');
          this.partnerService.showToast('Verification code sent to your email.');
        },
        error: (err) => {
          if (isDevMode()) {
            console.error('OTP request failed:', err);
          }
          this.isSubmittingLogin.set(false);
          const errorMsg = err.error?.metadata?.message || err.error?.message || '';
          if (errorMsg.toLowerCase().includes('activated') || errorMsg.toLowerCase().includes('already') || err.status === 409) {
            this.partnerService.showToast('already activated this account go to login page', 'error');
          } else if (err.status === 404 || errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('not partner')) {
            this.partnerService.showToast('your not partner go to contanus us page and enter the details', 'error');
          } else if (errorMsg) {
            this.partnerService.showToast(errorMsg, 'error');
          } else {
            this.partnerService.showToast('Failed to send verification code. Please try again.', 'error');
          }
        }
      });
    }
  }

  protected changeAuthTab(tab: 'signin' | 'signup') {
    if (tab === 'signup') {
      this.closeAllModals();
      this.openGetStarted();
    } else {
      this.authTab.set(tab);
    }
  }

  protected handleForgotPasswordSubmit() {
    const errors: Record<string, string> = {};
    if (this.forgotPasswordStep() === 1) {
      if (!this.forgotPasswordEmail) {
        errors['email'] = 'Enter a valid email address';
      } else if (!this.validateEmail(this.forgotPasswordEmail)) {
        errors['email'] = 'Enter a valid email address';
      }

      this.forgotPasswordErrors.set(errors);

      if (Object.keys(errors).length === 0) {
        this.isSubmittingForgotPassword.set(true);
        this.partnerApiService.requestPasswordReset(this.forgotPasswordEmail).subscribe({
          next: () => {
            this.isSubmittingForgotPassword.set(false);
            this.forgotPasswordStep.set(2);
            this.partnerService.showToast('Verification code sent to your email.');
          },
          error: (err) => {
            this.isSubmittingForgotPassword.set(false);
            const errMsg = err.error?.metadata?.message || err.error?.message || 'Failed to request password reset. Check if email exists.';
            this.partnerService.showToast(errMsg, 'error');
          }
        });
      }
    } else {
      const code = this.forgotPasswordOtpCode().join('');
      if (!code || code.length < 6) {
        errors['code'] = 'Verification code is required';
      }
      if (!this.forgotPasswordNewPassword) {
        errors['new_password'] = 'Password is required';
      } else if (!this.isForgotPasswordNewPasswordValid) {
        errors['new_password'] = 'Password does not meet requirements';
      }
      if (this.forgotPasswordNewPassword !== this.forgotPasswordConfirmPassword) {
        errors['confirm_password'] = 'Passwords do not match';
      }

      this.forgotPasswordErrors.set(errors);

      if (Object.keys(errors).length === 0) {
        this.isResettingPassword.set(true);
        const payload = {
          email: this.forgotPasswordEmail,
          code: code,
          new_password: this.forgotPasswordNewPassword
        };
        this.partnerApiService.resetPassword(payload).subscribe({
          next: () => {
            this.isResettingPassword.set(false);
            this.partnerService.showToast('Password reset successfully. Please log in with your new password.');
            // Go back to sign in
            this.isForgotPassword.set(false);
            this.forgotPasswordStep.set(1);
            this.forgotPasswordEmail = '';
            this.forgotPasswordOtpCode.set(['', '', '', '', '', '']);
            this.forgotPasswordNewPassword = '';
            this.forgotPasswordConfirmPassword = '';
            this.forgotPasswordErrors.set({});
          },
          error: (err) => {
            this.isResettingPassword.set(false);
            const errMsg = err.error?.metadata?.message || err.error?.message || 'Invalid or expired verification code';
            this.partnerService.showToast(errMsg, 'error');
          }
        });
      }
    }
  }

  protected onForgotPasswordOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    if (value && !/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    const currentOtp = [...this.forgotPasswordOtpCode()];
    currentOtp[index] = value;
    this.forgotPasswordOtpCode.set(currentOtp);
    this.forgotPasswordErrors.set({ ...this.forgotPasswordErrors(), code: '' });

    if (value && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-${index + 1}`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  protected onForgotPasswordOtpKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.forgotPasswordOtpCode()[index] && index > 0) {
      const prevInput = document.getElementById(`forgot-otp-${index - 1}`) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
        const currentOtp = [...this.forgotPasswordOtpCode()];
        currentOtp[index - 1] = '';
        this.forgotPasswordOtpCode.set(currentOtp);
      }
    }
  }

  protected onForgotPasswordOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    
    const pastedText = clipboardData.getData('text').trim();
    if (!pastedText) return;
    
    const digits = pastedText.replace(/\D/g, '').split('').slice(0, 6);
    if (digits.length === 0) return;
    
    const currentOtp = [...this.forgotPasswordOtpCode()];
    for (let i = 0; i < 6; i++) {
      if (digits[i] !== undefined) {
        currentOtp[i] = digits[i];
      }
    }
    this.forgotPasswordOtpCode.set(currentOtp);
    this.forgotPasswordErrors.set({ ...this.forgotPasswordErrors(), code: '' });

    const lastFocusedIndex = Math.min(digits.length - 1, 5);
    const lastInput = document.getElementById(`forgot-otp-${lastFocusedIndex}`) as HTMLInputElement;
    if (lastInput) {
      lastInput.focus();
    }
  }


  protected closeAllModals() {
    this.isContactOpen.set(false);
    this.isLoginOpen.set(false);
    this.isStandardLoginOpen.set(false);
    this.isGetStartedOpen.set(false);
    this.mobileMenuOpen.set(false);
    this.updateScrollLock(false);

    // Reset standard login
    this.standardLoginEmail = '';
    this.standardLoginPassword = '';
    this.standardLoginErrors.set({});
    this.isStandardLoginOtp.set(false);
    this.tempToken = '';
    this.tempNode = '';

    // Reset forgot password
    this.isForgotPassword.set(false);
    this.forgotPasswordStep.set(1);
    this.forgotPasswordEmail = '';
    this.forgotPasswordOtpCode.set(['', '', '', '', '', '']);
    this.forgotPasswordNewPassword = '';
    this.forgotPasswordConfirmPassword = '';
    this.forgotPasswordErrors.set({});

    this.showForgotPasswordNewPassword.set(false);
    this.showForgotPasswordConfirmPassword.set(false);


    // Reset contact
    this.contactForm = { name: '', company_name: '', email: '', phone: '', address: '', country: '', company_identity_number: '', message: '' };
    this.contactErrors.set({});
    this.contactSubmitted.set(false);
    this.contactSubmitAttempted = false;

    // Reset wizard
    this.startedStep.set(1);
    this.startedForm = {
      fullName: '', businessEmail: '', password: '', partnerType: 'integration',
      platformName: '', apiUsage: 'read-write', agreeToTerms: false
    };
    this.startedErrors.set({});
    this.startedSubmitted.set(false);

    // Reset login
    this.loginEmail = '';
    this.loginPassword = '';
    this.loginErrors.set({});
    this.loginSuccess.set(false);
    this.loginStep.set('credentials');
    this.otpCode.set(['', '', '', '', '', '']);
    this.otpError.set('');
  }

  protected onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z\s.'-]/g, '');
    this.contactForm.name = cleaned;
    input.value = cleaned;
    this.revalidateContactField('name');
  }

  protected onCompanyNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9\s&.,'()-]/g, '');
    this.contactForm.company_name = cleaned;
    input.value = cleaned;
    this.revalidateContactField('company_name');
  }

  protected onEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    this.contactForm.email = cleaned;
    input.value = cleaned;
    this.revalidateContactField('email');
  }

  protected onCompanyIdentityInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9]/g, '');
    this.contactForm.company_identity_number = cleaned;
    input.value = cleaned;
    this.revalidateContactField('company_identity_number');
  }

  protected onBusinessEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    this.startedForm.businessEmail = cleaned;
    input.value = cleaned;
  }

  protected onLoginEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    this.loginEmail = cleaned;
    input.value = cleaned;
  }

  protected onStandardLoginEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    this.standardLoginEmail = cleaned;
    input.value = cleaned;
  }

  protected onForgotPasswordEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    this.forgotPasswordEmail = cleaned;
    input.value = cleaned;
  }

  // Private validations (regexes shared with backend via partner-validation.constants)
  protected validateEmail(email: string): boolean {
    return V.EMAIL.REGEX.test(email);
  }

  private validatePhone(phone: string): boolean {
    return V.PHONE.REGEX.test(phone);
  }
}
